import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { from, fromEvent, lastValueFrom, mergeMap, takeUntil, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LOGGER } from '../../shared/logging/log.service';
import { REPRESENTATION_MODE } from '../../core/services/representation-mode.service';

const createAbortError = () => new DOMException('Aborted', 'AbortError');

const throwIfAborted = (signal?: AbortSignal) => {
	if (signal?.aborted) {
		throw createAbortError();
	}
};

// fromEvent never fires for a signal aborted before subscription; throwIfAborted covers that case.
const abortNotifier = (signal: AbortSignal) =>
	fromEvent(signal, 'abort').pipe(mergeMap(() => throwError(createAbortError)));

/**
 * BaseRequestService is a service that provides methods to make HTTP GET and POST requests
 * to the backend API, handling authentication and URL construction.
 *
 * It uses MSAL for authentication and HttpClient for making HTTP requests.
 */
@Injectable()
export class BaseRequestService {
	private readonly http = inject(HttpClient);
	private readonly msal = inject(MsalService);
	protected readonly logger = inject(LOGGER);
	private readonly representationMode = inject(REPRESENTATION_MODE, { optional: true });
	public readonly runningRequests = signal<string[]>([]);
	public readonly isLoading = computed(() => this.runningRequests().length > 0);
	private requestCounter = 0;

	protected get = async <T>(
		url: string,
		queryParams?: Record<string, unknown>,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		additionalOptions?: Record<string, any>,
	): Promise<T> => {
		this.runningRequests.update((requests) => [...requests, `get:${url}`]);
		const skipActAs = additionalOptions?.['skipActAs'] === true;
		const httpOptions = { ...(additionalOptions ?? {}) };
		delete httpOptions['skipActAs'];
		const headers = await this.buildHeaders(skipActAs);
		const resourceUrl = this.getApiUrl(url);

		const params = this.buildQueryParams(queryParams || {});

		try {
			return lastValueFrom(this.http.get<T>(resourceUrl, { headers, params, ...httpOptions }));
		} catch (e) {
			this.runningRequests.update((requests) => requests.filter((request) => request !== `get:${url}`));
			throw e;
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	protected deactivate = async <T>(url: string, queryParams?: Record<string, any>): Promise<T> => {
		this.runningRequests.update((requests) => [...requests, `deactivate:${url}`]);
		const headers = await this.buildHeaders();
		const resourceUrl = this.getApiUrl(url);

		const params = this.buildQueryParams(queryParams || {});

		try {
			return lastValueFrom(this.http.delete<T>(resourceUrl, { headers, params }));
		} catch (e) {
			this.runningRequests.update((requests) => requests.filter((request) => request !== `deactivate:${url}`));
			throw e;
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	protected remove = async <T>(url: string, queryParams?: Record<string, any>): Promise<T> => {
		this.runningRequests.update((requests) => [...requests, `delete:${url}`]);
		const headers = await this.buildHeaders();
		const resourceUrl = this.getApiUrl(url);

		const params = this.buildQueryParams(queryParams || {});

		try {
			return lastValueFrom(this.http.delete<T>(resourceUrl, { headers, params }));
		} catch (e) {
			this.runningRequests.update((requests) => requests.filter((request) => request !== `delete:${url}`));
			throw e;
		}
	};

	/**
	 * POST with optional cancellation. Pass `signal` in `additionalOptions`; an aborted call rejects with an
	 * `AbortError` and never reaches error handling.
	 */
	protected post = async <T, K>(
		url: string,
		body: K,
		queryParams?: Record<string, unknown>,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		additionalOptions?: Record<string, any>,
	): Promise<T> => {
		const requestKey = `post:${url}#${++this.requestCounter}`;
		this.runningRequests.update((requests) => [...requests, requestKey]);
		const skipActAs = additionalOptions?.['skipActAs'] === true;
		const httpOptions = { ...(additionalOptions ?? {}) };
		delete httpOptions['skipActAs'];
		const signal = httpOptions['signal'] as AbortSignal | undefined;
		delete httpOptions['signal'];
		try {
			throwIfAborted(signal);
			const headers = await this.buildHeaders(skipActAs);
			// Token acquisition awaits, so the signal may have flipped; no await may run between this check and subscribe.
			throwIfAborted(signal);
			const resourceUrl = this.getApiUrl(url);
			const params = this.buildQueryParams(queryParams || {});
			const request$ = this.http.post<T>(resourceUrl, body, { headers, params, ...httpOptions });
			return await lastValueFrom(signal ? request$.pipe(takeUntil(abortNotifier(signal))) : request$);
		} finally {
			this.runningRequests.update((requests) => requests.filter((request) => request !== requestKey));
		}
	};

	protected postObserve = <T, K>(url: string, body: K) => {
		return from(this.buildHeaders()).pipe(
			mergeMap((headers) => {
				const resourceUrl = this.getApiUrl(url);
				return this.http.post<T>(resourceUrl, body, { headers, observe: 'events' });
			}),
		);
	};

	protected put = async <T, K>(url: string, body: K, queryParams?: Record<string, unknown>): Promise<T> => {
		this.runningRequests.update((requests) => [...requests, `put:${url}`]);
		const headers = await this.buildHeaders();
		const resourceUrl = this.getApiUrl(url);
		const params = this.buildQueryParams(queryParams || {});
		try {
			return lastValueFrom(this.http.put<T>(resourceUrl, body, { headers, params }));
		} catch (e) {
			this.runningRequests.update((requests) => requests.filter((request) => request !== `put:${url}`));
			throw e;
		}
	};

	private buildQueryParams(params: Record<string, unknown>) {
		let httpParams = new HttpParams();
		Object.entries(params).forEach(([key, value]) => {
			if (Array.isArray(value)) {
				value.forEach((item) => {
					if (item !== undefined) {
						httpParams = httpParams.append(key, String(item));
					}
				});
			} else if (value !== undefined) {
				httpParams = httpParams.append(key, String(value));
			}
		});
		return httpParams;
	}

	private buildHeaders = async (skipActAs = false) => {
		const accounts = this.msal.instance.getAllAccounts();
		const response = await this.msal.instance.acquireTokenSilent({
			account: accounts[0],
			scopes: environment.auth.scopes,
		});
		const headers: Record<string, string> = {
			Authorization: `Bearer ${response.accessToken}`,
		};
		if (!skipActAs && this.representationMode?.isActive()) {
			const representedUserId = this.representationMode.mode().representedUserId;
			if (representedUserId) {
				headers['X-Act-As'] = representedUserId;
			}
		}
		return headers;
	};

	/**
	 * Injects the specific API URL needed for the call.
	 * @param url
	 * @returns a API URL string.
	 */
	private getApiUrl = (url: string) => {
		const baseApiUrl = `${environment.baseUrl}${environment.apiVersion}`;
		return `${baseApiUrl}${url}`;
	};
}
